import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useLanguage } from '@/contexts/LanguageContext';

interface PipelineStat {
  label: string;
  count: number;
}

interface StaffPipelineChartProps {
  data: PipelineStat[];
}

const COLORS = ['#3b82f6', '#06b6d4', '#6366f1', '#10b981', '#f59e0b', '#22c55e', '#ef4444'];

const T = {
  en: { title: 'Pipeline by Status', tooltipLabel: 'Count', tooltipSuffix: 'files' },
  fr: { title: 'Pipeline par Statut', tooltipLabel: 'Nombre', tooltipSuffix: 'dossiers' },
};

export const StaffPipelineChart: React.FC<StaffPipelineChartProps> = ({ data }) => {
  const { language } = useLanguage();
  const t = T[language] || T.en;

  return (
    <div className="bg-gray-800/50 rounded-xl p-6">
      <h3 className="text-lg font-semibold text-white mb-4">{t.title}</h3>
      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 5, right: 30, left: 100, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis type="number" stroke="#9ca3af" />
            <YAxis dataKey="label" type="category" stroke="#9ca3af" tick={{ fill: '#9ca3af', fontSize: 12 }} width={90} />
            <Tooltip
              contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#fff' }}
              formatter={(value: number) => [`${value} ${t.tooltipSuffix}`, t.tooltipLabel]}
            />
            <Bar dataKey="count" radius={[0, 4, 4, 0]}>
              {data.map((_, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        {data.slice(0, 4).map((stat, index) => (
          <div key={stat.label} className="bg-gray-900/50 rounded-lg p-3">
            <div className="w-2 h-2 rounded-full mb-2" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
            <p className="text-xs text-gray-400">{stat.label}</p>
            <p className="text-xl font-bold text-white">{stat.count}</p>
          </div>
        ))}
      </div>
    </div>
  );
};
